import { doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import chunk from 'lodash/chunk';
import { queryHashGenerator } from '../../utils/queryHash';
import { userAsPromise } from '../providers/userProvider';
import { IAthlete, ITest } from '../types';

export const bulkRetagTests = async (
  tests: ITest[],
  tags: string[]
): Promise<void> => {
  try {
    const batches = chunk(
      tests.map((t: ITest): { testId: string; queryHash: string } => {
        return {
          testId: t.id,
          queryHash: queryHashGenerator(t.testType.id, tags),
        };
      }),
      20
    );
    const user = await userAsPromise();

    for (const batch of batches) {
      const batchInProcess = writeBatch(user.services.firestore);
      for (const update of batch) {
        batchInProcess.update(
          doc(
            user.services.firestore,
            `orgs/${user.orgId}/tests/${update.testId}`
          ),
          {
            tags,
            queryHash: update.queryHash,
            writeTime: serverTimestamp(),
          }
        );
        batchInProcess.update(
          doc(
            user.services.firestore,
            `orgs/${user.orgId}/raw/${update.testId}`
          ),
          {
            tags,
          }
        );
      }
      await batchInProcess.commit();
    }
    return Promise.resolve();
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * @description bulk changes athlete
 * @param changeToId
 * @param tests
 * @returns
 */
export const bulkChangeAthlete = async (
  changeTo: IAthlete,
  tests: ITest[]
): Promise<void> => {
  try {
    const batches = chunk(
      tests.map((t: ITest): string => t.id),
      20
    );
    const user = await userAsPromise();

    for (const batch of batches) {
      const batchInProcess = writeBatch(user.services.firestore);
      for (const testId of batch) {
        batchInProcess.update(
          doc(user.services.firestore, `orgs/${user.orgId}/tests/${testId}`),
          {
            athleteId: changeTo.id,
            teams: changeTo.teams,
            groups: changeTo.groups ?? [],
            writeTime: serverTimestamp(),
          }
        );
        batchInProcess.update(
          doc(user.services.firestore, `orgs/${user.orgId}/raw/${testId}`),
          { athleteId: changeTo.id }
        );
      }
      await batchInProcess.commit();
    }
    return Promise.resolve();
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * @description bulk disables tests
 * @param testIds
 * @returns
 */
export const bulkDisable = async (testIds: string[]): Promise<void> => {
  try {
    const batches = chunk(testIds, 20);
    const user = await userAsPromise();

    for (const batch of batches) {
      const batchInProcess = writeBatch(user.services.firestore);
      for (const testId of batch) {
        batchInProcess.update(
          doc(user.services.firestore, `orgs/${user.orgId}/tests/${testId}`),
          {
            active: false,
            writeTime: serverTimestamp(),
          }
        );
      }
      await batchInProcess.commit();
    }
    return Promise.resolve();
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * @description bulk activates tests
 * @param testIds
 * @returns
 */
export const bulkActivate = async (testIds: string[]): Promise<void> => {
  try {
    const batches = chunk(testIds, 20);
    const user = await userAsPromise();

    for (const batch of batches) {
      const batchInProcess = writeBatch(user.services.firestore);
      for (const testId of batch) {
        batchInProcess.update(
          doc(user.services.firestore, `orgs/${user.orgId}/tests/${testId}`),
          {
            active: true,
            writeTime: serverTimestamp(),
          }
        );
      }
      await batchInProcess.commit();
    }
    return Promise.resolve();
  } catch (e) {
    return Promise.reject(e);
  }
};
